import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { Id } from "~/convex/_generated/dataModel";
import { SOCIAL_EVENTS } from "@/constants/analyticsEvents";
import { Switch } from "@/components/ui/switch";
import { Trash2 } from "lucide-react";
import { Upload } from "lucide-react";
import { User } from "~/types";
import { api } from "~/convex/_generated/api";
import { useAuthActions } from "@convex-dev/auth/react";
import { useDebouncedCallback } from "use-debounce";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useToast } from "../ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

// Helper function to get full URL for OAuth redirect
const getFullRedirectUrl = () => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/dashboard/settings`;
};

interface CreatorSettingsProps {
  user: User;
}

interface VideoTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform: "instagram" | "tiktok" | "youtube";
  handle: string;
  onStartTracking: (contractId: Id<"creatorContracts">) => void;
}

interface SocialConnectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  platform: "instagram" | "tiktok";
}

interface SocialAccount {
  handle: string;
  followers: number;
  posts: number;
  isPublic: boolean;
  trackAutomatically: Id<"creatorContracts">[];
}

interface SocialPlatformCardProps {
  platform: "instagram" | "tiktok" | "youtube";
  platformName: string;
  accounts: SocialAccount[];
  onRemoveAccount: (handle: string) => void;
  onUpdateVisibility: (handle: string, isPublic: boolean) => void;
  onStartTracking: (handle: string) => void;
  onStopTracking: (handle: string, contractId: Id<"creatorContracts">) => void;
  onConnectPlatform: () => void;
  getAppNameFromContractId: (contractId: Id<"creatorContracts">) => string;
  icon: React.ReactNode;
}

function SocialConnectDialog({
  isOpen,
  onClose,
  onConfirm,
  platform,
}: SocialConnectDialogProps) {
  const platformMessages = {
    instagram: {
      title: "Connect Instagram Account",
      warning:
        "You may see Odyssey Labs Limited during the connection process.",
      instruction: "This is our legal entity name - don't be alarmed by this.",
      note: "We're currently operating under this name while in beta.",
    },
    tiktok: {
      title: "Connect TikTok Account",
      warning:
        "Please ensure you're logged into the TikTok account you want to connect.",
      instruction:
        "You'll be redirected to TikTok to authorize the connection.",
      note: "We're currently in beta and continuously improving our integrations.",
    },
  };

  const message = platformMessages[platform];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {message.title}
            <span className="inline-flex items-center rounded-md bg-blue-500/10 px-2 py-1 text-xs font-medium text-blue-500 ring-1 ring-inset ring-blue-500/20">
              Beta
            </span>
          </DialogTitle>
          <DialogDescription className="text-left space-y-3">
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-1">
                Please read before continuing:
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-300">
                {message.warning}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-primary mb-1">
                What to do:
              </p>
              <p className="text-sm text-primary/70">{message.instruction}</p>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> {message.note}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>I understand, continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VideoTrackingModal({
  isOpen,
  onClose,
  platform,
  handle,
  onStartTracking,
}: VideoTrackingModalProps) {
  const { data: creatorCampaigns } = useQuery(
    convexQuery(api.dashboard.getCreatorCampaigns, {}),
  );

  const handleSelectContract = (contractId: Id<"creatorContracts">) => {
    onStartTracking(contractId);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Start Automatic Video Tracking</DialogTitle>
          <DialogDescription>
            Select which partnership contract you'd like to track videos for on{" "}
            <span className="font-semibold">@{handle}</span> ({platform}). All
            videos posted on this account will be automatically tracked until
            the contract ends.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto flex-1 min-h-0 scrollbar-hide">
          {creatorCampaigns &&
          creatorCampaigns.filter(
            (campaign) => campaign.campaign.type === "CiR",
          ).length > 0 ? (
            creatorCampaigns
              .filter((campaign) => campaign.campaign.type === "CiR")
              .map((campaign) => (
                <div
                  key={campaign.contract._id}
                  className="border border-border rounded-lg p-4"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src={campaign.app.logo}
                      alt={campaign.app.name}
                      className="h-8 w-8 rounded-full"
                    />
                    <div>
                      <h3 className="font-medium text-primary">
                        {campaign.campaign.name}
                      </h3>
                      <p className="text-sm text-primary/60">
                        Contract ends{" "}
                        {new Date(
                          campaign.contract.endDate,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleSelectContract(campaign.contract._id)}
                    className="w-full"
                    size="sm"
                  >
                    Track videos for this campaign
                  </Button>
                </div>
              ))
          ) : (
            <div className="text-center py-6">
              <p className="text-primary/60">
                No active Creator in Residence (CiR) campaigns found. Video
                tracking is only available for CiR partnerships.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SocialPlatformCard({
  platform,
  platformName,
  accounts,
  onRemoveAccount,
  onUpdateVisibility,
  onStartTracking,
  onStopTracking,
  onConnectPlatform,
  getAppNameFromContractId,
  icon,
}: SocialPlatformCardProps) {
  const isAccountTracking = (trackAutomatically: Id<"creatorContracts">[]) => {
    return trackAutomatically && trackAutomatically.length > 0;
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-primary">
        {platformName} {platform === "youtube" ? "Channels" : "Accounts"}
      </label>
      {accounts && accounts.length > 0 && (
        <div className="mb-2 flex flex-col gap-1">
          {accounts.map((account, index) => (
            <div key={index} className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary/60">
                  {platform === "youtube" ? "" : "@"}
                  {account.handle}
                </p>
                <p className="text-xs text-primary/40">
                  {account.followers.toLocaleString()} followers •{" "}
                  {account.posts.toLocaleString()} posts
                </p>
                {isAccountTracking(account.trackAutomatically) && (
                  <div className="space-y-1">
                    <p className="text-xs text-green-600">
                      Auto-tracking for:{" "}
                      {account.trackAutomatically
                        .map((contractId) =>
                          getAppNameFromContractId(contractId),
                        )
                        .join(", ")}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {account.trackAutomatically.map((contractId) => (
                        <Button
                          key={contractId}
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() =>
                            onStopTracking(account.handle, contractId)
                          }
                        >
                          Stop auto-tracking for{" "}
                          {getAppNameFromContractId(contractId)}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!isAccountTracking(account.trackAutomatically) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onStartTracking(account.handle)}
                  >
                    Auto-track videos
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-primary/60 hover:text-destructive"
                  onClick={() => onRemoveAccount(account.handle)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-primary/60">
                    {account.isPublic === false ? "Private" : "Public"}
                  </p>
                  <Switch
                    checked={account.isPublic}
                    onCheckedChange={() =>
                      onUpdateVisibility(account.handle, !account.isPublic)
                    }
                  />
                </div>
              </div>
            </div>
          ))}
          <p className="mt-1 text-xs text-primary/40">
            To add another {platformName} account, please log into the new
            account before connecting
          </p>
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        className="w-full bg-transparent"
        onClick={onConnectPlatform}
      >
        {icon}
        {accounts?.length
          ? `Connect Another ${platformName} ${platform === "youtube" ? "Channel" : "Account"}`
          : `Connect ${platformName}`}
      </Button>
    </div>
  );
}

export function CreatorSettings({ user }: CreatorSettingsProps) {
  const { data: creator } = useQuery(
    convexQuery(api.creators.getCreatorByUserId, {
      userId: user._id,
    }),
  );
  const { data: creatorCampaigns } = useQuery(
    convexQuery(api.dashboard.getCreatorCampaigns, {}),
  );
  const { mutateAsync: updateUserImage } = useMutation({
    mutationFn: useConvexMutation(api.app.updateUserImage),
  });
  const { mutateAsync: removeUserImage } = useMutation({
    mutationFn: useConvexMutation(api.app.removeUserImage),
  });
  const { mutateAsync: updateUserDetails } = useMutation({
    mutationFn: useConvexMutation(api.app.updateUserDetails),
  });
  const { mutateAsync: updateVisibilityOrRemoveSocialAccount } = useMutation({
    mutationFn: useConvexMutation(
      api.creators.updateVisbilityOrRemoveSocialAccount,
    ),
  });
  const { mutateAsync: startTrackingVideos } = useMutation({
    mutationFn: useConvexMutation(api.videos.startTrackingVideos),
  });
  const { mutateAsync: stopTrackingVideos } = useMutation({
    mutationFn: useConvexMutation(api.videos.stopTrackingVideos),
  });
  const { signIn } = useAuthActions();
  const { toast } = useToast();
  const { track } = useMixpanel();
  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await updateUserImage({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        imageId: (uploaded[0].response as any).storageId,
      });
    },
  });
  const { mutateAsync: updateCreator } = useMutation({
    mutationFn: useConvexMutation(api.creators.updateCreator),
  });
  const [localBio, setLocalBio] = useState(creator?.bio ?? "");
  const [localIsPublic, setLocalIsPublic] = useState(creator?.isPublic ?? true);
  const [localName, setLocalName] = useState(user.name ?? "");
  const [trackingModal, setTrackingModal] = useState<{
    isOpen: boolean;
    platform: "instagram" | "tiktok" | "youtube";
    handle: string;
  }>({
    isOpen: false,
    platform: "instagram",
    handle: "",
  });

  const [socialConnectDialog, setSocialConnectDialog] = useState<{
    isOpen: boolean;
    platform: "instagram" | "tiktok";
    onConfirm: () => void;
  }>({
    isOpen: false,
    platform: "instagram",
    onConfirm: () => {},
  });

  //   const showSocialsRecommendation =
  //     creatorId &&
  //     (!user.creatorSocials || Object.keys(user.creatorSocials).length === 0);

  // Helper function to get app name from contract ID
  const getAppNameFromContractId = (contractId: Id<"creatorContracts">) => {
    const campaign = creatorCampaigns?.find(
      (campaign) => campaign.contract._id === contractId,
    );
    return campaign?.campaign.name || "Unknown Campaign";
  };

  // Update local state immediately but debounce the API call
  const debouncedUpdateBio = useDebouncedCallback(async (newBio: string) => {
    await updateCreator({
      bio: newBio,
    });
  }, 1000);

  // Update local state immediately but debounce the API call for name
  const debouncedUpdateName = useDebouncedCallback(async (newName: string) => {
    await updateUserDetails({
      name: newName,
    });
  }, 1000);

  // Update localBio when creator data changes
  useEffect(() => {
    setLocalBio(creator?.bio ?? "");
  }, [creator?.bio]);

  // Update localIsPublic when creator data changes
  useEffect(() => {
    setLocalIsPublic(creator?.isPublic ?? true);
  }, [creator?.isPublic]);

  // Update localName when user data changes
  useEffect(() => {
    setLocalName(user.name ?? "");
  }, [user.name]);

  const handleRemoveSocialAccount = async (
    platform: "instagram" | "tiktok" | "youtube",
    handle: string,
  ) => {
    if (!creator) return;
    await updateVisibilityOrRemoveSocialAccount({
      creatorId: creator._id,
      platform,
      handle,
      isPublic: false,
      remove: true,
    });
  };

  const handleUpdateVisibility = async (
    platform: "instagram" | "tiktok" | "youtube",
    handle: string,
    isPublic: boolean,
  ) => {
    if (!creator) return;
    await updateVisibilityOrRemoveSocialAccount({
      creatorId: creator._id,
      platform,
      handle,
      isPublic,
      remove: false,
    });
  };

  const openTrackingModal = (
    platform: "instagram" | "tiktok" | "youtube",
    handle: string,
  ) => {
    setTrackingModal({
      isOpen: true,
      platform,
      handle,
    });
  };

  const closeTrackingModal = () => {
    setTrackingModal({
      isOpen: false,
      platform: "instagram",
      handle: "",
    });
  };

  const openSocialConnectDialog = (
    platform: "instagram" | "tiktok",
    onConfirm: () => void,
  ) => {
    setSocialConnectDialog({
      isOpen: true,
      platform,
      onConfirm,
    });
  };

  const closeSocialConnectDialog = () => {
    setSocialConnectDialog({
      isOpen: false,
      platform: "instagram",
      onConfirm: () => {},
    });
  };

  const handleStartTracking = async (contractId: Id<"creatorContracts">) => {
    try {
      await startTrackingVideos({
        contractId,
        platform: trackingModal.platform,
        handle: trackingModal.handle,
      });
      toast({
        title: "Video tracking started",
        description: `Videos posted on @${trackingModal.handle} will now be automatically tracked for this partnership.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to start video tracking",
        variant: "destructive",
      });
    }
  };

  const handleStopTracking = async (
    platform: "instagram" | "tiktok" | "youtube",
    handle: string,
    contractId: Id<"creatorContracts">,
  ) => {
    try {
      await stopTrackingVideos({
        contractId,
        platform,
        handle,
      });
      const appName = getAppNameFromContractId(contractId);
      toast({
        title: "Video tracking stopped",
        description: `Videos posted on @${handle} will no longer be automatically tracked for ${appName}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to stop video tracking",
        variant: "destructive",
      });
    }
  };

  // Platform icons
  const tiktokIcon = (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0011.14-4.02v-6.3a8.16 8.16 0 004.65 1.49v-3.39a4.85 4.85 0 01-1.2-.19z" />
    </svg>
  );

  const instagramIcon = (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );

  const youtubeIcon = (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );

  return (
    <div className="flex h-full w-full flex-col gap-6">
      {/* Avatar */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex w-full items-start justify-between rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">Your Avatar</h2>
            <p className="text-sm font-normal text-primary/60">
              This is your avatar. It will be displayed on your profile.
            </p>
          </div>
          <label
            htmlFor="avatar_field"
            className="group relative flex cursor-pointer overflow-hidden rounded-full transition active:scale-95"
          >
            <img
              src={user.avatarUrl}
              className="h-20 w-20 rounded-full object-cover"
              alt={user.name ?? user.email}
            />
            <div className="absolute z-10 hidden h-full w-full items-center justify-center bg-primary/40 group-hover:flex">
              <Upload className="h-6 w-6 text-secondary" />
            </div>
          </label>
          <input
            ref={fileInputRef}
            id="avatar_field"
            type="file"
            accept="image/*"
            className="peer sr-only"
            required
            tabIndex={user ? -1 : 0}
            onChange={async (event) => {
              if (!event.target.files) {
                return;
              }
              const files = Array.from(event.target.files);
              if (files.length === 0) {
                return;
              }
              startUpload(files);
            }}
          />
        </div>
        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            Click on the avatar to upload a custom one from your files.
          </p>
          {user.avatarUrl && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                removeUserImage({});
              }}
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* {showSocialsRecommendation && !appId && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200/20 bg-red-500/5 px-4 py-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-red-500">
              Connect Your Social Accounts
              <span className="ml-2 inline-flex items-center rounded-md bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500 ring-1 ring-inset ring-red-500/20">
                Highly Recommended
              </span>
            </p>
            <p className="mt-1 text-sm text-primary/60">
              Connecting your social accounts boosts your creator profile and
              increases your chances of being approved for partnerships.
            </p>
          </div>
        </div>
      )} */}

      {/* Connected Social Accounts */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">
            Connected Accounts
          </h2>
          <p className="text-sm font-normal text-primary/60">
            Manage your connected social media accounts
          </p>
        </div>
        {/* Disclaimer */}
        {creator?.tiktokHandles?.length ||
        creator?.instagramHandles?.length ||
        creator?.youtubeHandles?.length ? (
          <div className="flex w-full items-center gap-2 rounded-lg bg-blue-500/5 px-6 py-3 border-b border-border">
            <div className="flex-1">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> Disconnecting social media accounts will
                not remove any videos you've already uploaded to CreatorGrid.
              </p>
            </div>
          </div>
        ) : null}

        <div className="flex w-full flex-col gap-4 p-6 pt-6">
          <SocialPlatformCard
            platform="tiktok"
            platformName="TikTok"
            accounts={creator?.tiktokHandles ?? []}
            onRemoveAccount={(handle) =>
              handleRemoveSocialAccount("tiktok", handle)
            }
            onUpdateVisibility={(handle, isPublic) =>
              handleUpdateVisibility("tiktok", handle, isPublic)
            }
            onStartTracking={(handle) => openTrackingModal("tiktok", handle)}
            onStopTracking={(handle, contractId) =>
              handleStopTracking("tiktok", handle, contractId)
            }
            onConnectPlatform={() => {
              openSocialConnectDialog("tiktok", async () => {
                // Track social account connection attempt
                track(SOCIAL_EVENTS.CONNECTION_ATTEMPTED, {
                  platform: "tiktok",
                  user_id: user._id,
                  creator_id: creator?._id,
                  timestamp: new Date().toISOString(),
                  current_connected_platforms: [
                    ...(creator?.instagramHandles &&
                    creator.instagramHandles.length > 0
                      ? ["instagram"]
                      : []),
                    ...(creator?.youtubeHandles &&
                    creator.youtubeHandles.length > 0
                      ? ["youtube"]
                      : []),
                    ...(creator?.tiktokHandles &&
                    creator.tiktokHandles.length > 0
                      ? ["tiktok"]
                      : []),
                  ],
                  total_connected_accounts_before:
                    (creator?.instagramHandles?.length || 0) +
                    (creator?.youtubeHandles?.length || 0) +
                    (creator?.tiktokHandles?.length || 0),
                });

                closeSocialConnectDialog();
                await signIn("tiktok", {
                  redirectTo: getFullRedirectUrl(),
                });
              });
            }}
            getAppNameFromContractId={getAppNameFromContractId}
            icon={tiktokIcon}
          />

          <SocialPlatformCard
            platform="instagram"
            platformName="Instagram"
            accounts={creator?.instagramHandles ?? []}
            onRemoveAccount={(handle) =>
              handleRemoveSocialAccount("instagram", handle)
            }
            onUpdateVisibility={(handle, isPublic) =>
              handleUpdateVisibility("instagram", handle, isPublic)
            }
            onStartTracking={(handle) => openTrackingModal("instagram", handle)}
            onStopTracking={(handle, contractId) =>
              handleStopTracking("instagram", handle, contractId)
            }
            onConnectPlatform={() => {
              openSocialConnectDialog("instagram", async () => {
                // Track social account connection attempt
                track(SOCIAL_EVENTS.CONNECTION_ATTEMPTED, {
                  platform: "instagram",
                  user_id: user._id,
                  creator_id: creator?._id,
                  timestamp: new Date().toISOString(),
                  current_connected_platforms: [
                    ...(creator?.instagramHandles &&
                    creator.instagramHandles.length > 0
                      ? ["instagram"]
                      : []),
                    ...(creator?.youtubeHandles &&
                    creator.youtubeHandles.length > 0
                      ? ["youtube"]
                      : []),
                    ...(creator?.tiktokHandles &&
                    creator.tiktokHandles.length > 0
                      ? ["tiktok"]
                      : []),
                  ],
                  total_connected_accounts_before:
                    (creator?.instagramHandles?.length || 0) +
                    (creator?.youtubeHandles?.length || 0) +
                    (creator?.tiktokHandles?.length || 0),
                });

                closeSocialConnectDialog();
                await signIn("instagram", {
                  redirectTo: getFullRedirectUrl(),
                });
              });
            }}
            getAppNameFromContractId={getAppNameFromContractId}
            icon={instagramIcon}
          />

          <SocialPlatformCard
            platform="youtube"
            platformName="YouTube"
            accounts={creator?.youtubeHandles ?? []}
            onRemoveAccount={(handle) =>
              handleRemoveSocialAccount("youtube", handle)
            }
            onUpdateVisibility={(handle, isPublic) =>
              handleUpdateVisibility("youtube", handle, isPublic)
            }
            onStartTracking={(handle) => openTrackingModal("youtube", handle)}
            onStopTracking={(handle, contractId) =>
              handleStopTracking("youtube", handle, contractId)
            }
            onConnectPlatform={async () => {
              // openSocialConnectDialog("youtube", async () => {
              // Track social account connection attempt
              track(SOCIAL_EVENTS.CONNECTION_ATTEMPTED, {
                platform: "youtube",
                user_id: user._id,
                creator_id: creator?._id,
                timestamp: new Date().toISOString(),
                current_connected_platforms: [
                  ...(creator?.instagramHandles &&
                  creator.instagramHandles.length > 0
                    ? ["instagram"]
                    : []),
                  ...(creator?.youtubeHandles &&
                  creator.youtubeHandles.length > 0
                    ? ["youtube"]
                    : []),
                  ...(creator?.tiktokHandles && creator.tiktokHandles.length > 0
                    ? ["tiktok"]
                    : []),
                ],
                total_connected_accounts_before:
                  (creator?.instagramHandles?.length || 0) +
                  (creator?.youtubeHandles?.length || 0) +
                  (creator?.tiktokHandles?.length || 0),
              });

              closeSocialConnectDialog();
              await signIn("youtube", {
                redirectTo: getFullRedirectUrl(),
              });
              // });
            }}
            getAppNameFromContractId={getAppNameFromContractId}
            icon={youtubeIcon}
          />
        </div>
      </div>

      {/* Bio */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">Profile</h2>
          <p className="text-sm font-normal text-primary/60">
            Manage your creator profile information and visibility
          </p>
        </div>

        <div className="flex w-full flex-col gap-6 p-6 pt-0">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="name" className="text-sm font-medium text-primary">
              Display Name
            </label>
            <input
              id="name"
              type="text"
              className="w-full rounded-md border border-border bg-transparent p-3 text-sm text-primary outline-none focus:border-primary"
              placeholder="Enter your display name..."
              value={localName}
              onChange={(e) => {
                const newName = e.target.value;
                setLocalName(newName);
                debouncedUpdateName(newName);
              }}
            />
            <p className="text-xs text-primary/40">
              Your display name will be visible to app founders when you apply
              to partnerships
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="bio" className="text-sm font-medium text-primary">
              Bio
            </label>
            <textarea
              id="bio"
              className="min-h-[100px] w-full rounded-md border border-border bg-transparent p-3 text-sm text-primary outline-none focus:border-primary"
              placeholder="Write a short bio about yourself..."
              value={localBio}
              onChange={(e) => {
                const newBio = e.target.value;
                setLocalBio(newBio);
                debouncedUpdateBio(newBio);
              }}
            />
            <p className="text-xs text-primary/40">
              Your bio will be visible to app founders when you apply to create
              content
            </p>
          </div>

          <div className="flex items-center justify-between border-t border-border pt-6">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="isPublic"
                className="text-sm font-medium text-primary"
              >
                {localIsPublic ? "Public Profile" : "Private Profile"}
              </label>
              <p className="text-sm text-primary/60">
                {localIsPublic
                  ? "Your profile is visible to app founders and can be discovered"
                  : "Your profile is hidden from discovery but you can still apply to partnerships"}
              </p>
            </div>
            <Switch
              id="isPublic"
              checked={localIsPublic}
              onCheckedChange={async (checked) => {
                setLocalIsPublic(checked);
                await updateCreator({
                  isPublic: checked,
                });
              }}
            />
          </div>
        </div>

        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            Making your profile private won't affect existing partnerships or
            applications
          </p>
        </div>
      </div>

      {/* Video Tracking Modal */}
      <VideoTrackingModal
        isOpen={trackingModal.isOpen}
        onClose={closeTrackingModal}
        platform={trackingModal.platform}
        handle={trackingModal.handle}
        onStartTracking={handleStartTracking}
      />

      {/* Social Connect Dialog */}
      <SocialConnectDialog
        isOpen={socialConnectDialog.isOpen}
        onClose={closeSocialConnectDialog}
        onConfirm={socialConnectDialog.onConfirm}
        platform={socialConnectDialog.platform}
      />
    </div>
  );
}
